import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { BookOpen, CheckCircle2, XCircle, Hourglass, ShieldCheck, Target, MessageSquareQuote } from 'lucide-react';

const fmtH = (h) => {
  if (h === null || h === undefined) return '—';
  return Number.isInteger(h) ? `${h}h` : `${h.toFixed(1).replace('.', ',')}h`;
};

const VALEUR_STYLES = {
  A: 'bg-emerald-100 text-emerald-700',
  ECA: 'bg-amber-100 text-amber-700',
  NA: 'bg-red-100 text-red-700',
};

const VALEUR_LABELS = {
  A: 'Acquis',
  ECA: 'En cours',
  NA: 'Non acquis',
};

const CompetencesSection = ({ competences }) => {
  if (!competences || competences.length === 0) return null;
  return (
    <div className="mt-5" data-testid="competences-section">
      <div className="flex items-center gap-2 mb-2">
        <Target className="w-4 h-4 text-indigo-500" />
        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Mes savoir-faire & connaissances, recommandations</h4>
      </div>
      <div className="space-y-2">
        {competences.map((c, i) => (
          <div key={`${c.test_type}-${c.test_id}-${i}`} className="bg-slate-50 rounded-lg p-3" data-testid={`competence-test-${c.test_id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold flex-shrink-0 ${c.test_type === 'qcm' ? 'bg-indigo-100 text-indigo-700' : 'bg-purple-100 text-purple-700'}`}>
                  {c.test_type === 'qcm' ? 'QCM' : 'ÉVAL'}
                </span>
                <p className="text-xs font-medium text-slate-700 truncate">
                  {c.test_code && <span className="font-mono text-slate-400 mr-1">{c.test_code}</span>}
                  {c.test_titre}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {c.score_pct !== null && c.score_pct !== undefined && (
                  <span className={`text-[10px] font-bold ${c.score_pct >= 70 ? 'text-emerald-600' : c.score_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{c.score_pct}%</span>
                )}
                {c.date && <span className="text-[9px] text-slate-400">{new Date(c.date).toLocaleDateString('fr-FR')}</span>}
              </div>
            </div>
            {(c.resultats || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {c.resultats.map((r, ri) => (
                  <span key={`${r.competence_titre}-${ri}`} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${VALEUR_STYLES[r.valeur] || 'bg-slate-100 text-slate-500'}`}>
                    {r.competence_titre} · {VALEUR_LABELS[r.valeur] || '—'}
                  </span>
                ))}
              </div>
            )}
            {c.recommandations && (
              <div className="flex items-start gap-1.5 mt-2 text-[11px] text-slate-600 bg-white rounded p-2 border border-slate-100">
                <MessageSquareQuote className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0 mt-0.5" />
                <p className="italic">{c.recommandations}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const ModulesTab = ({ modulesData, validationsData }) => {
  const dossiers = modulesData?.dossiers || [];
  const valDossiers = validationsData?.dossiers || [];

  if (dossiers.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="py-12 text-center">
          <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500">Aucun module disponible</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {dossiers.map((d) => {
        const valDossier = valDossiers.find(v => v.dossier_id === d.dossier_id);
        const validations = valDossier?.validations || [];
        const validatedModuleIds = new Set(validations.map(v => v.module_id));
        const nbValides = d.modules.filter(m => validatedModuleIds.has(m.module_id)).length;
        return (
        <Card key={d.dossier_id} className="border-0 shadow-sm" data-testid={`modules-dossier-${d.dossier_id}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span>{d.dossier_intitule || d.formation_titre}</span>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">{d.dossier_code}</Badge>
                {nbValides > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-700 text-xs" data-testid={`badge-modules-valides-${d.dossier_id}`}>
                    <ShieldCheck className="w-3 h-3 mr-1" /> {nbValides} module(s) validé(s)
                  </Badge>
                )}
                <Badge className="bg-indigo-100 text-indigo-700 text-xs" data-testid={`modules-pct-${d.dossier_id}`}>
                  {d.totaux.pct}% réalisé
                </Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Résumé heures */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-slate-400 mb-0.5">Heures prévues</p>
                <p className="text-lg font-bold text-slate-800">{fmtH(d.totaux.heures_prevues)}</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-emerald-600 mb-0.5 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Réalisées</p>
                <p className="text-lg font-bold text-emerald-700">{fmtH(d.totaux.heures_realisees)}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-red-600 mb-0.5 flex items-center gap-1"><XCircle className="w-3 h-3" /> Manquées</p>
                <p className="text-lg font-bold text-red-700">{fmtH(d.totaux.heures_manquees)}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-amber-600 mb-0.5 flex items-center gap-1"><Hourglass className="w-3 h-3" /> Restantes</p>
                <p className="text-lg font-bold text-amber-700">{fmtH(d.totaux.heures_restantes)}</p>
              </div>
            </div>

            {/* Tableau modules */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid={`modules-table-${d.dossier_id}`}>
                <thead>
                  <tr className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                    <th className="px-3 py-2 text-left font-medium">Module</th>
                    <th className="px-3 py-2 text-center font-medium">Prévues</th>
                    <th className="px-3 py-2 text-center font-medium">Réalisées</th>
                    <th className="px-3 py-2 text-center font-medium">Manquées</th>
                    <th className="px-3 py-2 text-center font-medium">Restantes</th>
                    <th className="px-3 py-2 text-left font-medium w-40">Progression</th>
                    <th className="px-3 py-2 text-center font-medium">Validation</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {d.modules.map((m) => {
                    const validation = validations.find(v => v.module_id === m.module_id);
                    return (
                    <tr key={m.module_id} data-testid={`module-row-${m.module_id}`}>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium text-slate-800">{m.titre || m.code || '—'}</p>
                          {m.hors_programme && <Badge variant="outline" className="text-[9px] text-violet-600 border-violet-300">Hors programme</Badge>}
                        </div>
                        {m.code && m.titre && <p className="text-[10px] text-slate-400">{m.code}</p>}
                      </td>
                      <td className="px-3 py-2.5 text-center font-medium text-slate-700">{fmtH(m.heures_prevues)}</td>
                      <td className="px-3 py-2.5 text-center font-semibold text-emerald-600">{fmtH(m.heures_realisees)}</td>
                      <td className={`px-3 py-2.5 text-center font-semibold ${m.heures_manquees > 0 ? 'text-red-600' : 'text-slate-300'}`}>
                        {m.heures_manquees > 0 ? fmtH(m.heures_manquees) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-center font-medium text-amber-600">{fmtH(m.heures_restantes)}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <Progress value={m.pct} className="h-1.5 flex-1" />
                          <span className="text-[10px] font-semibold text-slate-500 w-8 text-right">{m.pct}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {validation ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" title={validation.validated_at ? `Validé le ${new Date(validation.validated_at).toLocaleDateString('fr-FR')}` : ''} data-testid={`badge-valide-${m.module_id}`}>
                            <ShieldCheck className="w-3 h-3 mr-0.5" /> Validé
                          </Badge>
                        ) : (
                          <span className="text-[10px] text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                  })}
                  {d.modules.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-6 text-slate-400">Aucun module défini pour cette formation</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Compétences & recommandations */}
            <CompetencesSection competences={valDossier?.competences} />
          </CardContent>
        </Card>
      );
      })}
    </div>
  );
};
